import { getUserFriendlyError } from '../utils/errors';
import { reportError } from './errorTracking';
import { requestNotificationsRefresh } from '../utils/notificationEvents';
import * as meetingApi from './meetingApi';

// The ONE web implementation of "end this meeting" for screens that are
// not the live page (the Meetings list and the meeting details page). It
// is the same PATCH status=ended the live page's End Meeting uses
// (backend: meeting_service.apply_meeting_update, then usage metering and
// the post-meeting analysis chain) - there is no second end path.
//
// The backend decides what happened, never the caller. Whether the PATCH
// succeeded, was refused, or failed, the meeting is re-read and the
// outcome is taken from what the backend now says:
//
//   * ended by this request                  -> ended
//   * refused as already ended (the backend's abandonment reaper, or
//     another tab, got there first)          -> ended, and NOT an error:
//                                               the state the user wanted
//                                               has been reached
//   * genuinely failed, still active         -> not ended, with a message
//
// Returns { ended, meeting, errorMessage }. `meeting` is the freshly read
// record, or null if it could not be re-read; a successful PATCH with an
// unreadable follow-up still counts as ended (the PATCH is itself the
// backend's confirmation), whereas a failed PATCH is never assumed to
// have worked.
export async function endMeeting(meetingId) {
  let failure = null;
  try {
    await meetingApi.updateMeeting(meetingId, { status: 'ended' });
  } catch (err) {
    failure = err;
  }

  let meeting = null;
  try {
    meeting = await meetingApi.getMeeting(meetingId);
  } catch (_) {
    // Decided below from the PATCH outcome alone.
  }

  const ended = meeting ? meeting.status === 'ended' : failure === null;

  if (ended) {
    // The "Meeting still active" notification is retired server-side as
    // soon as the meeting is not active - have the bell look again.
    requestNotificationsRefresh();
    return { ended: true, meeting, errorMessage: '' };
  }

  // A genuine failure to end (still active afterwards) - category only.
  reportError('meeting_end', failure || new Error('MEETING_END_NOT_CONFIRMED'));

  return {
    ended: false,
    meeting,
    errorMessage: failure
      ? getUserFriendlyError(failure, 'Unable to end meeting. Please try again.')
      : 'Unable to end meeting. Please try again.',
  };
}
